import { IQuestionModel } from '../Question/QuestionModel';
import { IResponse } from '../Response/Response';
import { SnapshotOut } from 'mobx-state-tree';
import { isPhoneNumberValid } from '../../../components/Answer/AnswerFreeText/AnswerPhoneNumber';

export interface ValidatorContext {
  /** First answer key of a previously-answered question in the same survey, if any. */
  findAnswerKey?: (questionKey: string) => string | undefined;
}

type Validator = (response: SnapshotOut<IResponse>) => boolean;
type ValidatorBank = Partial<Record<string, Validator>>;

// WPO Counselling — the only service for which chooseTopics is optional.
// All other services (e.g. Practical Support) require at least one topic.
const COUNSELLING_SERVICE_TYPE_KEY = '12';

const validateOnKey: ValidatorBank = {

  /* only valid for a valid phone number */
  phoneNumber: r => !!r.freeText && isPhoneNumberValid(r.freeText),
  videocallPhoneNumber: r => !!r.freeText && isPhoneNumberValid(r.freeText),

  /* only valid when an option is selected */
  chooseDate: r => r.answers.some(a => a.meta?.some(m => m.key === 'option')),
};

const validateOnType: ValidatorBank = {
  instruction: () => true,
  /* drawn by their own frame (sleep/mood diary), which fills the response */
  special: () => true,
  singleSelect: r => r.answers.length > 0,
  singleSelectWithRadio: r => r.valueSonar != null && r.importance != null,
  multiSelect: r => r.answers.length > 0,
  /* any non-zero length string is valid */
  freeText: r => !!r.freeText && r.freeText.length > 0,
};

export const validateSurveyResponse = (
  response: SnapshotOut<IResponse>,
  question?: IQuestionModel,
  ctx?: ValidatorContext,
) => {
  if (!question) {
    return false;
  }
  // chooseTopics: question.skippable=true (kept for counselling), but every
  // other WPO service requires at least one topic — override here.
  if (response.questionKey === 'chooseTopics') {
    const serviceTypeKey = ctx?.findAnswerKey?.('chooseService');
    if (serviceTypeKey && serviceTypeKey !== COUNSELLING_SERVICE_TYPE_KEY) {
      return response.answers.length > 0;
    }
  }
  if (question.skippable) {
    return true;
  }
  const onKey = response.questionKey ? validateOnKey[response.questionKey] : undefined;
  if (onKey) {
    console.log('validating on key', response.questionKey);
    return onKey(response);
  }
  const onType = question.type ? validateOnType[question.type] : undefined;
  if (onType) {
    return onType(response);
  }
  return false;
};
